import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectTheme } from '../slices/preferencesSlice';
function StatsCard({stats, columns = 1, divided = false, variant = 'default'}) {
  const columnClasses = {
    1: 'lg:grid-cols-1',
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    6: 'lg:grid-cols-6',
  };

  const columnClass = columnClasses[columns] || 'lg:grid-cols-1';

  // detect theme (light/dark/system) and compute effective dark mode
  const theme = useSelector(selectTheme);
  const [effectiveDark, setEffectiveDark] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const compute = () => setEffectiveDark(theme === 'dark' || (theme === 'system' && mq.matches));
    compute();
    const listener = () => theme === 'system' && compute();
    if (mq.addEventListener) mq.addEventListener('change', listener); else mq.addListener(listener);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', listener); else mq.removeListener(listener); };
  }, [theme]);

  const mapTextForDark = (textColor) => {
    if (!textColor) return 'text-gray-300';
    const lc = textColor.toLowerCase();
    if (lc.includes('indigo')) return 'text-brand-400';
    if (lc.includes('blue')) return 'text-blue-400';
    if (lc.includes('green') || lc.includes('emerald')) return 'text-emerald-400';
    if (lc.includes('teal') || lc.includes('cyan')) return 'text-cyan-400';
    if (lc.includes('purple') || lc.includes('violet')) return 'text-brand-400';
    if (lc.includes('pink') || lc.includes('rose')) return 'text-pink-400';
    if (lc.includes('yellow') || lc.includes('amber')) return 'text-amber-400';
    if (lc.includes('orange')) return 'text-orange-400';
    if (lc.includes('red')) return 'text-red-400';
    return 'text-gray-300';
  };

  const mapBadgeForDark = (badgeColor) => {
    if (!badgeColor) return badgeColor;
    const lc = badgeColor.toLowerCase();
    // prefer stronger backgrounds + readable text in dark mode
    if (lc.includes('green')) return 'bg-green-600 text-white';
    if (lc.includes('gray')) return 'bg-gray-700 text-gray-100';
    if (lc.includes('yellow')) return 'bg-yellow-500 text-gray-900';
    if (lc.includes('red')) return 'bg-red-600 text-white';
    if (lc.includes('blue')) return 'bg-brand-600 text-white';
    // fallback to the provided value
    return badgeColor;
  };

  const renderItem = ({title, count, note, noteHighlight, icon: Icon, bgColor = '', textColor = 'text-gray-700', badge, badgeColor = "bg-green-100 text-green-800"}, index) => {
    const borderClass = 'border border-gray-200/70 dark:border-gray-600/70 shadow-sm';
    const appliedBadgeColor = effectiveDark ? mapBadgeForDark(badgeColor) : badgeColor;
    const iconColor = effectiveDark ? mapTextForDark(textColor) : textColor;

    if (divided) {
      return (
        <div key={index} className={`flex-1 flex flex-col gap-1 px-5 py-4 min-w-0 ${bgColor}`}>
          <div className="flex items-center gap-1.5">
            <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${textColor}`} aria-hidden="true"/>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 truncate">{title}</span>
          </div>
          <div className="text-2xl font-mono font-medium text-gray-800 dark:text-gray-100 leading-tight">{count}</div>
          {note && (
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">{note}</p>
          )}
          {badge && (
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${appliedBadgeColor} dark:bg-opacity-90 whitespace-nowrap self-start mt-0.5`}>
              {badge}
            </span>
          )}
        </div>
      );
    }

    if (variant === 'compact') {
      return (
        <div
          key={index}
          className={`flex items-center gap-1.5 py-1.5 ${bgColor}`}
          title={note ? `${title}: ${note}` : title}
        >
          <Icon className={`h-4 w-4 flex-shrink-0 ${iconColor}`} aria-hidden="true" />
          <div className="text-[15px] font-mono font-semibold leading-none text-gray-700 dark:text-gray-100 tabular-nums">
            {count}
          </div>
          {badge && (
            <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${appliedBadgeColor} dark:bg-opacity-90 whitespace-nowrap`}>
              {badge}
            </span>
          )}
        </div>
      );
    }

    // Flat KPI cell: borderless, monochrome — a clean showcase block that sits
    // as intentional contrast beside a dense data table (no nested boxes, no
    // decorative icon colours).
    if (variant === 'kpi') {
      return (
        <div
          key={index}
          className="flex flex-col"
          title={note ? `${title}: ${note}` : title}
        >
          <div className="text-2xl sm:text-3xl font-mono font-semibold tabular-nums leading-none text-gray-900 dark:text-gray-100">
            {typeof count === 'number' ? count.toLocaleString() : count}
          </div>
          <div className="mt-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {title}
          </div>
        </div>
      );
    }

    return (
      <div
        key={index}
        className={`relative flex flex-col items-start justify-between p-2 sm:p-3 rounded-xl bg-surface dark:bg-gray-870 ${borderClass} ${bgColor}`}
        style={{minHeight: window.innerWidth < 640 ? '80px' : '100px'}}
      >
        <div className="flex items-center gap-1.5 mb-1.5 sm:mb-2 w-full">
          <Icon className={`h-4 w-4 flex-shrink-0 ${effectiveDark ? mapTextForDark(textColor) : textColor}`} aria-hidden="true"/>
          <span className={`text-xs sm:text-sm font-semibold truncate ${effectiveDark ? mapTextForDark(textColor) : textColor}`}>{title}</span>
        </div>
        <div className="ml-0.5 sm:ml-1 text-xl sm:text-2xl font-mono font-medium text-gray-600 dark:text-gray-200 p-0.5 sm:p-1">{count}</div>
        <div className="w-full flex items-end justify-between gap-2">
          {note && (
            <p className="ml-0.5 sm:ml-1 text-xs text-gray-800 dark:text-gray-300 mt-0.5 sm:mt-1 p-0.5 sm:p-1 flex-1 min-w-0">
              {typeof note === 'string' && noteHighlight ? (
                <>
                  {note.split(noteHighlight)[0]}
                  <span className="text-red-600 dark:text-red-400 font-bold">{noteHighlight}</span>
                  {note.split(noteHighlight)[1]}
                </>
              ) : (
                note
              )}
            </p>
          )}
          {badge && (
            <span
              className={`inline-flex items-center rounded-full px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium ${appliedBadgeColor} dark:bg-opacity-90 whitespace-nowrap flex-shrink-0`}>
              {badge}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (divided) {
    return <>{stats.map(renderItem)}</>;
  }

  if (variant === 'compact') {
    return (
      <div className="rounded-xl border border-gray-200/70 bg-surface px-4 py-3 shadow-sm dark:border-gray-600/70 dark:bg-gray-870">
        {stats.map(renderItem)}
      </div>
    );
  }

  if (variant === 'kpi') {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
        {stats.map(renderItem)}
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-2 sm:grid-cols-2 ${columnClass} gap-4`}>
      {stats.map(renderItem)}
    </div>
  );
}

export default StatsCard;
